"use client";

import Image from "next/image";
import Link from "next/link";
import { socialLinks, importantLinks, serviceLinks, usefulLinks } from "@/data/navigation";

export default function Footer() {
  return (
    <footer className="bg-surface">
      {/* CTA Marquee Banner */}
      <div className="py-4 bg-red-accent overflow-hidden">
        <div className="marquee-track" style={{ "--marquee-duration": "30s" } as React.CSSProperties}>
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="flex-shrink-0 mx-8 text-sm font-semibold text-white whitespace-nowrap">
              Why risk it with the wrong partner? Get 100% value and guarantee. Don&apos;t miss out — Secure your brand&apos;s future today.
              <span className="mx-4">✦</span>
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-[960px] mx-auto px-6 pt-16 pb-8">
        {/* Newsletter + Social */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-16">
          <div className="flex items-center gap-3">
            <Image src="/assets/devcubix-logo-dark.png" alt="Dev Cubix" width={140} height={50} className="object-contain dark:hidden block" />
            <Image src="/assets/devcubix-logo-footer.png" alt="Dev Cubix" width={140} height={50} className="object-contain hidden dark:block" />
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <input
              type="email"
              placeholder="Your email here"
              className="px-4 py-2.5 bg-surface-light border border-foreground/10 rounded-full text-sm text-foreground placeholder:text-foreground/30 w-full sm:w-56 focus:outline-none focus:border-purple/50"
            />
            <button className="px-6 py-2.5 bg-purple text-white rounded-full text-sm font-semibold hover:bg-purple/80 transition-colors whitespace-nowrap">
              Subscribe
            </button>
          </div>
          <div className="flex items-center gap-3">
            {socialLinks.map((s) => (
              <Link
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Follow us on ${s.name}`}
                className="w-10 h-10 rounded-full border border-foreground/10 flex items-center justify-center hover:border-purple/50 transition-colors"
              >
                <Image src={s.icon} alt={s.name} width={18} height={18} className="theme-invert opacity-60" />
              </Link>
            ))}
          </div>
        </div>

        {/* Location — dark card with red gradient glow + watermark */}
        <div className="relative rounded-3xl overflow-hidden mb-16 h-72 md:h-80 bg-surface-light">
          {/* Red gradient glow from bottom */}
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 70% 60% at 50% 100%, rgba(225,29,36,0.2) 0%, rgba(225,29,36,0.05) 50%, transparent 80%)" }} />
          {/* "devcubix" watermark */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
            <span className="text-[60px] sm:text-[100px] md:text-[160px] font-bold font-ui leading-none" style={{
              background: "linear-gradient(180deg, rgba(225,29,36,0.25) 0%, rgba(225,29,36,0.08) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>
              devcubix
            </span>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <a href="https://www.google.com/maps/place/DevCubix+Private+Ltd./@33.5198,73.0898,17z/" target="_blank" rel="noopener noreferrer" className="bg-surface/70 backdrop-blur-md border border-foreground/10 rounded-2xl px-8 py-5 text-center hover:border-foreground/20 transition-colors cursor-pointer block">
              <p className="text-lg font-bold text-foreground mb-1">Pakistan</p>
              <p className="text-xs text-foreground/70">2nd Floor, Building 150, Block D,</p>
              <p className="text-xs text-foreground/70">Civic Center Bahria Town, Rawalpindi 46000</p>
            </a>
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-16">
          <div>
            <h4 className="text-sm font-semibold mb-4">Important Links</h4>
            <ul className="space-y-2">
              {importantLinks.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="text-sm text-foreground/50 hover:text-foreground transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-4">Services</h4>
            <ul className="space-y-2">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="text-sm text-foreground/50 hover:text-foreground transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-4">Useful Links</h4>
            <ul className="space-y-2">
              {usefulLinks.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="text-sm text-foreground/50 hover:text-foreground transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-4">Contact</h4>
            <ul className="space-y-2">
              <li><Link href="mailto:info@devcubix.com" className="text-sm text-foreground/50 hover:text-foreground transition-colors">info@devcubix.com</Link></li>
              <li><Link href="https://wa.me/+923190708852" className="text-sm text-foreground/50 hover:text-foreground transition-colors">+92 319 0708852</Link></li>
            </ul>
          </div>
        </div>

        {/* Partner logos row — like DM page_10 */}
        <div className="border-t border-card-border pt-8 pb-8">
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-8 md:gap-14">
            {[
              { name: "Framer", label: "Professional Partner", logo: "/assets/badges/Framer-Partner.svg" },
              { name: "Webflow", label: "Professional Partner", logo: "/assets/badges/Webflow-Partner.svg" },
              { name: "Behance", label: "Top Team On Behance", logo: "/assets/badges/Behance-Top.svg" },
              { name: "Clutch", label: "Reviewed 5★ ★★★★★", logo: "/assets/badges/Clutch.svg" },
              { name: "Google", label: "Reviewed 5★ ★★★★★", logo: "/assets/badges/Google.svg" },
            ].map((partner) => (
              <div key={partner.name} className="flex flex-col items-center gap-2">
                <Image src={partner.logo} alt={partner.name} width={100} height={32} className="h-7 w-auto object-contain theme-invert opacity-70" />
                <span className="text-[10px] text-foreground/30">{partner.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-card-border pt-6 pb-20 sm:pb-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground/30">&copy; 2026, Dev Cubix, All Rights Reserved.</p>
          <span className="text-xs text-foreground/30 cursor-default">Privacy Policy</span>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="text-xs text-foreground/30 hover:text-foreground/60 transition-colors flex items-center gap-1"
          >
            ● Back to Top ↑
          </button>
        </div>
      </div>

      {/* Giant brand watermark at bottom — like DM's "designmonks" */}
      <div className="relative overflow-hidden h-[250px] md:h-[350px]">
        {/* Green gradient glow */}
        <div className="absolute bottom-0 left-0 right-0 h-full" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(225,29,36,0.06) 40%, rgba(225,29,36,0.15) 70%, rgba(225,29,36,0.25) 100%)" }} />
        {/* Large "devcubix" text */}
        <div className="absolute bottom-[-30px] md:bottom-[-50px] left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none select-none">
          <span className="text-[80px] sm:text-[120px] md:text-[200px] lg:text-[260px] font-bold font-ui leading-none" style={{
            background: "linear-gradient(180deg, color-mix(in srgb, var(--foreground) 15%, transparent) 0%, color-mix(in srgb, var(--foreground) 5%, transparent) 60%, transparent 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>
            devcubix
          </span>
        </div>
      </div>
    </footer>
  );
}
